import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { MAX_BULK_DATASETS, fetchBulkCandidates } from '@/features/catalog-browser';
import {
  isProfileJobStatus,
  isProfileRequestOutcome,
} from '@/shared/api/generated/catalog';
import type {
  CatalogRow,
  DatasetFacts,
  ProfileJobStatus,
  ProfileRequestOutcome,
} from '@/shared/api/generated/catalog';
import {
  forgetIdempotencyKey,
  getPage,
  idempotencyKeyFor,
  post,
} from '@/shared/api/http';

import { AUTO_PROFILE_KEY } from './autoProfile';

/* A source's datasets with what the page needs to say about each: the row, its profile facts, and
 * the profile queue's latest job.
 *
 * **The rows and facts are the catalogue browser's own walk** (`fetchBulkCandidates`), so this page
 * and that one enumerate a source identically and cannot disagree about which datasets it has. The
 * walk is bounded at `MAX_BULK_DATASETS` and the bound is reported as `truncated` rather than read as
 * the end of the estate.
 */

export interface SourceDatasets {
  readonly rows: readonly CatalogRow[];
  readonly facts: ReadonlyMap<string, DatasetFacts>;
  /** False when a facts read failed: "never profiled" is then not a claim the page can make. */
  readonly factsRead: boolean;
  readonly truncated: boolean;
}

export const SOURCE_DATASETS_KEY = ['catalog', 'source-datasets'] as const;
const PROFILE_JOBS_KEY = ['catalog', 'profile-jobs'] as const;

/** How many ids one job-state read carries — the route's own maximum, as for `/datasets/facts`. */
const JOB_CHUNK = 200;

/** How many datasets one profile request may name — the route's `MAX_REQUESTED_DATASETS`. */
const MAX_NAMED_PER_REQUEST = 500;

export function useSourceDatasets(connectionId: string): UseQueryResult<SourceDatasets> {
  return useQuery({
    queryKey: [...SOURCE_DATASETS_KEY, connectionId],
    queryFn: async () => {
      const { rows, facts } = await fetchBulkCandidates(connectionId, '');
      return {
        rows,
        facts: facts ?? new Map<string, DatasetFacts>(),
        factsRead: facts !== undefined,
        truncated: rows.length >= MAX_BULK_DATASETS,
      };
    },
    retry: false,
  });
}

/** The profile queue's latest job for each of these datasets, keyed by dataset.
 *
 * **Polled only while something is outstanding**, every four seconds: a queued or running profile
 * changes state on the sweep's own clock, and a reader watching the column wants to see it move.
 * An idle source sends nothing. Chunked at the route's maximum, so a source of two thousand tables
 * is ten small reads rather than one URL a server is entitled to refuse.
 */
export function useProfileJobs(
  connectionId: string,
  datasetIds: readonly string[],
): UseQueryResult<ReadonlyMap<string, ProfileJobStatus>> {
  return useQuery({
    queryKey: [...PROFILE_JOBS_KEY, connectionId, datasetIds.length, datasetIds[0] ?? ''],
    enabled: datasetIds.length > 0,
    retry: false,
    queryFn: async () => {
      const jobs = new Map<string, ProfileJobStatus>();
      for (let offset = 0; offset < datasetIds.length; offset += JOB_CHUNK) {
        const params = new URLSearchParams();
        for (const id of datasetIds.slice(offset, offset + JOB_CHUNK))
          params.append('dataset_id', id);
        const page = await getPage(
          `/v1/catalog/connections/${encodeURIComponent(connectionId)}/profile-jobs?${params.toString()}`,
          isProfileJobStatus,
          'profile job',
        );
        for (const job of page.items) jobs.set(job.dataset_id, job);
      }
      return jobs;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return false;
      for (const job of data.values())
        if (job.state === 'pending' || job.state === 'running') return 4000;
      return false;
    },
  });
}

export interface ProfileRequest {
  readonly connectionId: string;
  /** The tables to profile, or `undefined` for every live dataset of the source. */
  readonly datasetIds?: readonly string[] | undefined;
}

/** Queue profiles through the source's profile queue.
 *
 * **Queued, not run**, which is why this replaces a per-dataset fan-out on this page: the queue
 * drains at the source's own pace — its concurrency bound and the per-tick cap — so "Profile all"
 * on a large source is a batch that drains, not hundreds of scans arriving at once. It survives
 * leaving the page, and every row shows where its job is.
 */
export function useRequestProfiles(): UseMutationResult<
  ProfileRequestOutcome,
  Error,
  ProfileRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ connectionId, datasetIds }: ProfileRequest) => {
      const send = async (
        ids: readonly string[] | undefined,
        scope: string,
      ): Promise<ProfileRequestOutcome> => {
        const key = idempotencyKeyFor('profile-request', `${connectionId}:${scope}`);
        try {
          return await post(
            `/v1/catalog/connections/${encodeURIComponent(connectionId)}/profile-requests`,
            key,
            isProfileRequestOutcome,
            'profile request',
            ids === undefined ? {} : { dataset_ids: ids },
          );
        } finally {
          /* Forgotten whatever happened: the next press is a new request, not a replay. */
          forgetIdempotencyKey('profile-request', `${connectionId}:${scope}`);
        }
      };
      if (datasetIds === undefined) return send(undefined, 'all');
      /* A selection past the route's per-request maximum goes as several requests rather than
         being refused: the person chose these tables, and the bound is the route's, not theirs. */
      let named = 0;
      let queued = 0;
      for (let offset = 0; offset < datasetIds.length; offset += MAX_NAMED_PER_REQUEST) {
        const chunk = datasetIds.slice(offset, offset + MAX_NAMED_PER_REQUEST);
        const outcome = await send(chunk, String(offset));
        named += outcome.named ?? chunk.length;
        queued += outcome.queued;
      }
      return { connection_id: connectionId, named, queued };
    },
    onSuccess: (_outcome, { connectionId }) => {
      void client.invalidateQueries({ queryKey: [...PROFILE_JOBS_KEY, connectionId] });
      void client.invalidateQueries({ queryKey: [...AUTO_PROFILE_KEY, connectionId] });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
    retry: false,
  });
}
